/**
 * Image Resize Utilities
 * Functions for client-side image processing and optimization
 */

/**
 * Resize an image file to target dimensions while maintaining aspect ratio
 * 
 * @param {File} file - Original image file
 * @param {number} maxWidth - Maximum width in pixels (default: 640)
 * @param {number} maxHeight - Maximum height in pixels (default: 640) 
 * @param {number} quality - JPEG quality 0-1 (default: 0.85)
 * @returns {Promise<File>} - Resized image as a File object
 */
export const resizeImage = async (file, maxWidth = 640, maxHeight = 640, quality = 0.85) => {
  try {
    // Validate input - check if it's a proper File object
    if (!file) {
      console.log('No file provided to resizeImage');
      return null;
    }
    
    // Check if this is an existing image object (not a File) - skip resizing
    if (!file.type && (file.url || file.id)) {
      console.log(`Existing image object detected, skipping resize: ${file.name || 'unknown'}`);
      return file;
    }
    
    // Validate that it's a proper File with image type
    if (!file.type || !file.type.startsWith('image/')) {
      console.log(`Invalid file or not an image: ${file?.name || 'unknown'}, type: ${file?.type}`);
      return null;
    }

    // Skip SVGs as they don't need resizing
    if (file.type === 'image/svg+xml') {
      console.log(`SVG file detected, no need to resize: ${file.name}`);
      return file;
    }
    
    // Check if image is small enough to not need resizing
    // Small images under 150KB don't need resizing in most cases
    if (file.size < 150 * 1024) {
      console.log(`Image is already small (${Math.round(file.size / 1024)} KB), skipping resize: ${file.name}`);
      return file;
    }

    // Detect browser
    const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
    
    // For Firefox, we'll skip resizing entirely to avoid black images
    if (isFirefox) {
      console.log(`Firefox detected, skipping image resize for: ${file.name} to prevent black image issue`);
      // Skip resizing completely in Firefox as canvas operations can cause issues
      return file;
    }
    
    // Convert file to image and resize using Promises for proper async handling
    return await new Promise((resolve, reject) => {
      // Create a file reader to read the file as array buffer
      const reader = new FileReader();
      
      reader.onload = (event) => {
        // Create a blob from the array buffer
        const blob = new Blob([event.target.result]);
        const blobUrl = URL.createObjectURL(blob);
        
        // Create an image element to load the blob
        const img = new Image();
        img.src = blobUrl;
        
        img.onload = () => {
          // Calculate new dimensions while maintaining aspect ratio
          let width = img.width;
          let height = img.height;
          
          // Store original dimensions for logging
          const originalWidth = width;
          const originalHeight = height;
          
          // Determine which dimension needs to be scaled
          if (width > height) {
            if (width > maxWidth) {
              height = Math.round(height * (maxWidth / width));
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round(width * (maxHeight / height));
              height = maxHeight;
            }
          }
          
          // Skip resizing if the image is already smaller than target dimensions
          if (originalWidth <= maxWidth && originalHeight <= maxHeight) {
            console.log(`Image ${file.name} already smaller than target dimensions, skipping resize`);
            URL.revokeObjectURL(blobUrl); // Clean up
            resolve(file);
            return;
          }

          try {
            // Create a canvas to draw the resized image
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            
            // Draw the resized image on the canvas
            const ctx = canvas.getContext('2d');
            
            // Clear canvas first 
            ctx.clearRect(0, 0, width, height);
            
            // Set canvas background to white (prevents transparency issues)
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, width, height);
            
            // Draw the image
            ctx.drawImage(img, 0, 0, width, height);
            
            // Convert canvas to blob
            const mimeType = file.type; // Keep original image type
            
            canvas.toBlob((blob) => {
              URL.revokeObjectURL(blobUrl); // Clean up
              
              if (!blob) {
                console.error(`Failed to create blob for ${file.name}, using original`);
                resolve(file);
                return;
              }
              
              // Create new file from blob
              const resizedFile = new File([blob], file.name, {
                type: mimeType,
                lastModified: Date.now()
              });
              
              // Log results
              console.log(`Resized image: ${file.name}`, {
                originalSize: `${Math.round(file.size / 1024)} KB`,
                newSize: `${Math.round(resizedFile.size / 1024)} KB`,
                originalDimensions: `${originalWidth}x${originalHeight}`,
                newDimensions: `${width}x${height}`,
                reductionPercent: Math.round((1 - (resizedFile.size / file.size)) * 100) + '%'
              });
              
              resolve(resizedFile);
            }, mimeType, quality);
          } catch (canvasError) {
            console.error('Canvas operation failed:', canvasError);
            URL.revokeObjectURL(blobUrl); // Clean up
            // Always fall back to original file on error
            resolve(file);
          }
        };
        
        img.onerror = (error) => {
          console.error('Error loading image for resize:', error);
          URL.revokeObjectURL(blobUrl); // Clean up
          resolve(file); // Fall back to original file
        };
      };
      
      reader.onerror = (error) => {
        console.error('Error reading file for resize:', error);
        resolve(file); // Fall back to original file
      };
      
      // Start reading the file
      reader.readAsArrayBuffer(file);
    });
  } catch (error) {
    console.error('Unexpected error during image resize:', error);
    return file; // Fall back to original file
  }
};

/**
 * Resize multiple image files in parallel
 * 
 * @param {File[]} files - Array of image files
 * @param {number} maxWidth - Maximum width in pixels (default: 640)
 * @param {number} maxHeight - Maximum height in pixels (default: 640) 
 * @param {number} quality - JPEG quality 0-1 (default: 0.85)
 * @returns {Promise<File[]>} - Array of resized image files
 */
export const resizeImageFiles = async (files, maxWidth = 640, maxHeight = 640, quality = 0.85) => {
  console.log(`Resizing ${files.length} images in batch mode`);
  const resizePromises = Array.from(files).map(file => resizeImage(file, maxWidth, maxHeight, quality));
  return Promise.all(resizePromises);
};
